import { z } from 'zod'

import type { EmployeesListParams, TeamsListParams } from '@gta/api-client'

/**
 * 组织管理域(员工 / 团队)URL 状态契约(唯一真源)。
 *
 * 红线:筛选 / 排序 / 分页一律进 search params —— 冷加载任意一条 `/employees?...`
 * 或 `/teams?...` 都必须完整还原页面状态,组件内不得用 useState 存这些状态。
 * 表单页(`/employees/new` 等)刻意复用同一份列表 schema:表单只是列表的一次「下钻」,
 * 提交完 / 取消都要能带着原筛选回到列表,把筛选态写进表单页 URL 是最省事也最抗冷加载的做法。
 *
 * 取值面严格对齐后端手写 query params(gta/apps/account_apps/accounts/api/views.py 组织域段):
 *   employees: team / is_active / search / ordering / page / page_size
 *   teams:     parent / search / ordering / page / page_size
 * 非法值一律 `.catch()` 降级成默认值,保证脏 URL 不白屏(后端对非法值是 400)。
 */

/** 空串 / 全空白 = 未筛选(而不是「筛选空值」),统一降级成 undefined 以便从 URL 里消失。 */
const optionalText = z.string().trim().min(1).optional().catch(undefined)

/* -------------------------------------------------------------------------- */
/* 员工                                                                        */
/* -------------------------------------------------------------------------- */

/** 后端 EMPLOYEE_ORDERING_FIELDS 白名单的正反序全展开。 */
export const EMPLOYEE_ORDERING_VALUES = [
  'display_name',
  '-display_name',
  'is_active',
  '-is_active',
  'create_time',
  '-create_time',
  'update_time',
  '-update_time',
] as const

export type EmployeeOrdering = (typeof EMPLOYEE_ORDERING_VALUES)[number]

/** 可点排序的列(与上面白名单的字段名一致)。 */
export type EmployeeSortField = 'display_name' | 'is_active' | 'create_time'

/** 后端 apply_ordering 的兜底也是 display_name,前后端默认档保持一致。 */
export const DEFAULT_EMPLOYEE_ORDERING: EmployeeOrdering = 'display_name'

export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 20

/** 后端 DemoPageNumberPagination.max_page_size = 100。 */
export const MAX_PAGE_SIZE = 100
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

/** 「未分组」档:后端 `team=none` 只出未分配团队的员工(见 EmployeeListView.get_queryset)。 */
export const TEAM_FILTER_NONE = 'none'

export const employeesSearchSchema = z.object({
  /** 团队 resource_id;`none` = 只看未分组员工。 */
  team: optionalText,
  /**
   * 在职筛选。TanStack Router 默认用 JSON.parse 解析 search 值,`?is_active=false`
   * 回来就是布尔 false —— 故这里直接收布尔,不必再走字符串哨兵。
   */
  is_active: z.boolean().optional().catch(undefined),
  /** 模糊搜索:姓名 / 员工资源号 / 登录账号(输入框 300ms 防抖后写入)。 */
  search: optionalText,
  // `.default()` 让这几项在**输入**侧可省略(Link/navigate 不必每次带全套 search),
  // `.catch()` 兜住脏值;两者叠加 = 「URL 里没有就用默认,有但不合法也用默认」。
  ordering: z
    .enum(EMPLOYEE_ORDERING_VALUES)
    .default(DEFAULT_EMPLOYEE_ORDERING)
    .catch(DEFAULT_EMPLOYEE_ORDERING),
  page: z.coerce.number().int().min(1).default(DEFAULT_PAGE).catch(DEFAULT_PAGE),
  page_size: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE)
    .catch(DEFAULT_PAGE_SIZE),
})

export type EmployeesSearch = z.infer<typeof employeesSearchSchema>

/** 等于默认值的参数不进 URL(stripSearchParams,A4),裸 /employees 就是干净首屏。 */
export const EMPLOYEES_SEARCH_DEFAULTS = {
  ordering: DEFAULT_EMPLOYEE_ORDERING,
  page: DEFAULT_PAGE,
  page_size: DEFAULT_PAGE_SIZE,
} satisfies Partial<EmployeesSearch>

/**
 * URL search params → 后端 query params。
 * 未设置的筛选项整个键都不下发(而不是下发 undefined),
 * 这样 react-query 的 queryKey 才不会因为「有键但值是 undefined」多出一份缓存。
 */
export function toEmployeesListParams(search: EmployeesSearch): EmployeesListParams {
  return {
    page: search.page,
    page_size: search.page_size,
    ordering: search.ordering,
    ...(search.team === undefined ? {} : { team: search.team }),
    ...(search.is_active === undefined ? {} : { is_active: search.is_active }),
    ...(search.search === undefined ? {} : { search: search.search }),
  }
}

export function hasActiveEmployeeFilters(search: EmployeesSearch): boolean {
  return search.team !== undefined || search.is_active !== undefined || search.search !== undefined
}

/* -------------------------------------------------------------------------- */
/* 团队                                                                        */
/* -------------------------------------------------------------------------- */

/** 后端 TEAM_ORDERING_FIELDS 白名单的正反序全展开。 */
export const TEAM_ORDERING_VALUES = [
  'name',
  '-name',
  'create_time',
  '-create_time',
  'update_time',
  '-update_time',
] as const

export type TeamOrdering = (typeof TEAM_ORDERING_VALUES)[number]
export type TeamSortField = 'name' | 'create_time'

export const DEFAULT_TEAM_ORDERING: TeamOrdering = 'name'

/**
 * 团队列表按 parent 关系缩进成树,**必须拿到全集**才能把子团队挂到各自的上级下面
 * (只取一页会让第 2 页的子团队变成孤儿行)。后端内部团队至多两层、量级是几十,
 * 故一次性取满 max_page_size;真超了在页脚提示,而不是画一棵残缺的树。
 */
export const TEAM_TREE_PAGE_SIZE = MAX_PAGE_SIZE

export const teamsSearchSchema = z.object({
  /** 模糊搜索:团队名 / 团队资源号(输入框 300ms 防抖后写入)。 */
  search: optionalText,
  ordering: z.enum(TEAM_ORDERING_VALUES).default(DEFAULT_TEAM_ORDERING).catch(DEFAULT_TEAM_ORDERING),
})

export type TeamsSearch = z.infer<typeof teamsSearchSchema>

export const TEAMS_SEARCH_DEFAULTS = {
  ordering: DEFAULT_TEAM_ORDERING,
} satisfies Partial<TeamsSearch>

export function toTeamsListParams(search: TeamsSearch): TeamsListParams {
  return {
    page: DEFAULT_PAGE,
    page_size: TEAM_TREE_PAGE_SIZE,
    ordering: search.ordering,
    ...(search.search === undefined ? {} : { search: search.search }),
  }
}

export function hasActiveTeamFilters(search: TeamsSearch): boolean {
  return search.search !== undefined
}

/* -------------------------------------------------------------------------- */
/* 排序循环(两域同一套规则:同字段反转,换字段则名称升序、其余降序)               */
/* -------------------------------------------------------------------------- */

function cycle<T extends string>(current: string, field: string, ascendingFirst: boolean): T {
  if (current === field) return `-${field}` as T
  if (current === `-${field}`) return field as T
  return (ascendingFirst ? field : `-${field}`) as T
}

export function nextEmployeeOrdering(current: EmployeeOrdering, field: EmployeeSortField): EmployeeOrdering {
  return cycle<EmployeeOrdering>(current, field, field === 'display_name')
}

export function nextTeamOrdering(current: TeamOrdering, field: TeamSortField): TeamOrdering {
  return cycle<TeamOrdering>(current, field, field === 'name')
}

/** 当前排序方向;该字段没在排序中则返回 null。 */
export function sortDirectionOf(current: string, field: string): 'asc' | 'desc' | null {
  if (current === field) return 'asc'
  if (current === `-${field}`) return 'desc'
  return null
}
